import { Node } from './node-base';
import { CorporationCFC, Disregarded, Partnership } from './entity-nodes';
import { EntityNodeOptions, Jurisdiction, ShapeType } from './types';

let textMeasureCanvasContext: CanvasRenderingContext2D | null = null;

export function measureLabelText(text: string): number {
    if (typeof document !== 'undefined') {
        textMeasureCanvasContext ??= document.createElement('canvas').getContext('2d');
    }
    if (!textMeasureCanvasContext) {
        return String(text || '').length * 6.5;
    }
    textMeasureCanvasContext.font = '600 12px Arial, Inter, sans-serif';
    return textMeasureCanvasContext.measureText(String(text || '')).width;
}

export function getFlagImageUrl(countryCode?: string): string | null {
    const code = String(countryCode || '').trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) return null;
    if (typeof document !== 'undefined' && document.baseURI) {
        return new URL(`flags/4x3/${code.toLowerCase()}.svg`, document.baseURI).href;
    }
    return `flags/4x3/${code.toLowerCase()}.svg`;
}

export function resolveDiagramType(options: EntityNodeOptions): string {
    if (options.diagramType) {
        const raw = options.diagramType.trim();
        const lower = raw.toLowerCase();
        if (lower === 'uscorporation' || lower === 'us_corporation') return 'UsCorporation';
        if (lower === 'cfc') return 'Cfc';
        if (lower === 'partnership') return 'Partnership';
        if (lower === 'disregardedentity' || lower === 'dre' || lower === 'disregarded') return 'DisregardedEntity';
        if (lower === 'branch') return 'Branch';
        if (lower === 'external' || lower === 'thirdparty') return 'External';
        return raw;
    }

    switch (options.shape) {
        case ShapeType.Partnership:
            return 'Partnership';
        case ShapeType.Branch:
            return 'Branch';
        case ShapeType.DRE:
            return 'DisregardedEntity';
        case ShapeType.ThirdParty:
            return 'External';
        default:
            return options.jurisdiction === Jurisdiction.US ? 'UsCorporation' : 'Cfc';
    }
}

export function applyOrgChartStylingToNode(node: Node, options: EntityNodeOptions): void {
    if (options.entityIds && options.entityIds.length > 0) {
        node.entityIds = [...options.entityIds];
    } else if (options.entityId) {
        node.entityIds = [options.entityId];
    }

    const diagramType = resolveDiagramType(options);

    // 1. Dimensions matching Org Chart Cytoscape styling
    const width = 225;
    const height = diagramType === 'Partnership' ? 116
        : diagramType === 'Branch' ? 90
        : 82;
    node.resize(width, height);

    // 2. Colors and borders matching Org Chart Cytoscape styles
    let fillColor: string;
    let borderColor: string;
    let textColor: string;

    switch (diagramType) {
        case 'UsCorporation':
            fillColor = '#0784ad';
            borderColor = '#036282';
            textColor = '#ffffff';
            break;
        case 'Cfc':
            fillColor = '#84c318';
            borderColor = '#56840c';
            textColor = '#ffffff';
            break;
        case 'Partnership':
            fillColor = '#fbbf24';
            borderColor = '#b77907';
            textColor = '#17324d';
            break;
        case 'DisregardedEntity':
            fillColor = '#f97316';
            borderColor = '#9a3412';
            textColor = '#ffffff';
            break;
        case 'Branch':
            fillColor = '#7c3aed';
            borderColor = '#5422a8';
            textColor = '#ffffff';
            break;
        case 'External':
        case 'ThirdParty':
            fillColor = '#ffffff';
            borderColor = '#64748b';
            textColor = '#17324d';
            break;
        default:
            if (options.jurisdiction === Jurisdiction.US) {
                fillColor = '#0784ad';
                borderColor = '#036282';
                textColor = '#ffffff';
            } else {
                fillColor = '#84c318';
                borderColor = '#56840c';
                textColor = '#ffffff';
            }
            break;
    }

    node.setColor(fillColor);
    node.setStrokeColor(borderColor);
    node.setFontColor(textColor);
    node.attr('body/strokeWidth', 2);

    if (node instanceof CorporationCFC || node instanceof Disregarded) {
        node.attr('body/rx', 4);
        node.attr('body/ry', 4);
    }

    if (diagramType === 'DisregardedEntity' || node instanceof Disregarded) {
        node.attr('body/fill', '#f97316');
        node.attr('body/stroke', '#9a3412');
        node.attr('body2/stroke', '#9a3412');
        node.attr('body2/strokeWidth', 2);
        node.attr('body2/fill', 'none');
        node.attr('body2/cx', 'calc(0.5*w)');
        node.attr('body2/cy', 'calc(0.5*h)');
        node.attr('body2/rx', 'calc(0.5*w - 1)');
        node.attr('body2/ry', 'calc(0.5*h - 1)');
    }

    if (diagramType === 'Partnership' || node instanceof Partnership) {
        node.attr('body/points', '0,calc(h), calc(0.5*w),0, calc(w),calc(h)');
        node.attr('label/y', 'calc(0.6*h)');
    }

    // 3. Multi-line label matching Org Chart: [Name, ID, Country]
    const rawLabel = options.label || '';
    const nameText = rawLabel.split('\n')[0].trim();
    const entityId = options.entityId || (options.entityIds && options.entityIds[0]) || '';
    const countryName = options.country || options.countryCode || '';
    const countryWithSpacer = options.countryCode ? `\u00a0\u00a0\u00a0${countryName}` : countryName;

    const lines: string[] = [nameText];
    if (entityId) lines.push(entityId);
    if (countryWithSpacer) lines.push(countryWithSpacer);
    const formattedLabel = lines.filter(Boolean).join('\n');

    node.attr('label/text', formattedLabel);
    node.attr('label/fill', textColor);
    node.attr('label/fontSize', 12);
    node.attr('label/font-size', 12);
    node.attr('label/fontWeight', 600);
    node.attr('label/font-weight', 600);
    node.attr('label/fontFamily', 'Arial, Inter, sans-serif');
    node.attr('label/font-family', 'Arial, Inter, sans-serif');
    node.attr('label/lineHeight', '1.3em');
    node.attr('label/line-height', '1.3em');
    node.attr('label/textAnchor', 'middle');
    node.attr('label/text-anchor', 'middle');
    node.attr('label/textVerticalAnchor', 'middle');
    node.attr('label/text-vertical-anchor', 'middle');
    node.attr('label2/text', '');
    node.attr('label2/display', 'none');

    // Ensure markup includes flag and appropriate body shape
    const currentMarkup = (node.get('markup') || (node as any).markup || []) as Array<{ tagName: string; selector: string }>;
    if (!currentMarkup.some(m => m.selector === 'flag')) {
        const newMarkup = [...currentMarkup];
        if (diagramType === 'External' || diagramType === 'ThirdParty') {
            const bodyIdx = newMarkup.findIndex(m => m.selector === 'body');
            if (bodyIdx >= 0) {
                newMarkup[bodyIdx] = { tagName: 'rect', selector: 'body' };
            }
        }
        const labelIdx = newMarkup.findIndex(m => m.selector === 'label');
        if (labelIdx >= 0) {
            newMarkup.splice(labelIdx, 0, { tagName: 'image', selector: 'flag' });
        } else {
            newMarkup.push({ tagName: 'image', selector: 'flag' });
        }
        node.set('markup', newMarkup);
    }

    // 4. SVG Flag rendering matching Org Chart
    const flagUrl = options.countryCode ? getFlagImageUrl(options.countryCode) : null;
    if (flagUrl) {
        const iconWidth = 18;
        const iconHeight = 13.5;
        const flagOffset = diagramType === 'Partnership' ? 22
            : diagramType === 'Branch' ? 16.8
            : 15.2;

        const spacerWidth = measureLabelText('\u00a0\u00a0\u00a0');
        const countryWidth = measureLabelText(countryName);
        const flagX = width / 2 + (spacerWidth - countryWidth) / 2 - 2 - iconWidth;
        const flagY = height / 2 + flagOffset - iconHeight / 2;

        node.attr('flag/href', flagUrl);
        node.attr('flag/xlinkHref', flagUrl);
        node.attr('flag/x', Math.round(flagX * 100) / 100);
        node.attr('flag/y', Math.round(flagY * 100) / 100);
        node.attr('flag/width', iconWidth);
        node.attr('flag/height', iconHeight);
        node.attr('flag/display', 'block');
        node.attr('flag/preserveAspectRatio', 'xMidYMid meet');
    } else {
        node.attr('flag/display', 'none');
        node.attr('flag/href', '');
        node.attr('flag/xlinkHref', '');
        node.attr('flag/width', 0);
        node.attr('flag/height', 0);
    }

    // Store metadata on cell
    node.prop('diagramType', diagramType);
    if (options.countryCode) node.prop('countryCode', options.countryCode);
    if (options.country) node.prop('country', options.country);
}
